import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  LayoutDashboard, 
  Calendar, 
  Briefcase, 
  Image,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Database,
  Shield,
  Mail
} from 'lucide-react'
import { useStore } from '../../store/useStore'

const AdminNavbar = () => {
  const { user, logout, messages } = useStore()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const navLinks = [
    { path: '/admin', label: 'Tableau de Bord', icon: LayoutDashboard },
    { path: '/admin/banners', label: 'Bannières', icon: Calendar },
    { path: '/admin/jobs', label: 'Offres d\'Emploi', icon: Briefcase },
    { path: '/admin/applications', label: 'Candidatures', icon: Users },
    { path: '/admin/messages', label: 'Messages', icon: Mail },
    { path: '/admin/gallery', label: 'Galeries', icon: Image },
    { path: '/admin/admins', label: 'Administrateurs', icon: Shield },
    { path: '/admin/data', label: 'Données RGPD', icon: Database },
    { path: '/admin/settings', label: 'Paramètres', icon: Settings }
  ]

  const unreadMessagesCount = messages.filter(msg => msg.status === 'unread').length

  return (
    <nav className="bg-primary-500 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/admin" className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <span className="text-primary-600 font-bold text-sm">RM</span>
            </div>
            <span className="text-white font-semibold">
              Admin Panel
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => {
              const Icon = link.icon
              const isActive = location.pathname === link.path
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{link.label}</span>
                  {link.path === '/admin/messages' && unreadMessagesCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadMessagesCount}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:block text-white text-sm">
              <p className="font-medium">{user?.name}</p>
              <p className="text-white/80 text-xs">{user?.email}</p>
            </div>
            
            <button
              onClick={logout}
              className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Déconnexion</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-white/80 hover:text-white"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-white/20"
          >
            <div className="py-4 space-y-2">
              {navLinks.map((link) => {
                const Icon = link.icon
                const isActive = location.pathname === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{link.label}</span>
                    {link.path === '/admin/messages' && unreadMessagesCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-auto">
                        {unreadMessagesCount}
                      </span>
                    )}
                  </Link>
                )
              })}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  )
}

export default AdminNavbar
